"use client";

import { Label } from "@/components/ui/label";
import { useInteractions } from "../../../hooks/interactionsProvider";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import TimestampInput from "@/components/ui/timestamp-input";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

const attemptsOptions = [
    {
        value: 1,
        keyName: "interaction-modal.settings.attempts-limit.1",
        defaultValue: "1",
    },
    {
        value: 2,
        keyName: "interaction-modal.settings.attempts-limit.2",
        defaultValue: "2",
    },
    {
        value: 3,
        keyName: "interaction-modal.settings.attempts-limit.3",
        defaultValue: "3",
    },
    {
        value: 4,
        keyName: "interaction-modal.settings.attempts-limit.4",
        defaultValue: "4",
    },
    {
        value: 5,
        keyName: "interaction-modal.settings.attempts-limit.5",
        defaultValue: "5",
    },
    {
        value: 10,
        keyName: "interaction-modal.settings.attempts-limit.10",
        defaultValue: "10",
    },
];

export default function SettingsOptions() {
    const { dir } = useLocale();
    const {
        mode,
        selectedInteraction,
        updateInteraction,
        scenes,
        getInteractionGap,
        getLomdaRewindSceneId,
        setLomdaRewindSceneId,
    } = useInteractions();

    const isLomda = mode === "lomdaV2";

    const showAttemptsField =
        selectedInteraction?.type === "close" ||
        selectedInteraction?.type === "open" ||
        selectedInteraction?.type === "audio";

    const showRewindField =
        selectedInteraction?.type === "close" ||
        selectedInteraction?.type === "open" ||
        selectedInteraction?.type === "audio";

    const showAllowSkippingField =
        selectedInteraction?.type === "close" ||
        selectedInteraction?.type === "open" ||
        selectedInteraction?.type === "form" ||
        selectedInteraction?.type === "rating" ||
        selectedInteraction?.type === "audio" ||
        selectedInteraction?.type === "cloze" ||
        selectedInteraction?.type === "match" ||
        selectedInteraction?.type === "aiChat";

    const showHideCorrectAnswerField =
        selectedInteraction?.type === "close" ||
        selectedInteraction?.type === "open";

    return (
        <div className="flex flex-col gap-6">
            {showAttemptsField && (
                <div className="flex flex-row w-full gap-4">
                    <div className="flex w-full flex-col gap-2">
                        <Label>
                            {t(
                                "interaction-modal.settings.select-attempt-limit.label",
                                undefined,
                                undefined
                            )}
                        </Label>
                        <Select
                            dir={dir}
                            value={selectedInteraction?.content.attemptLimit?.toString()}
                            onValueChange={(value) => {
                                updateInteraction(
                                    selectedInteraction!.id,
                                    // both objects are exactly the same, this is just for ts because audio content is different than open/close question content
                                    selectedInteraction.type === "audio"
                                        ? {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  attemptLimit: Number(value),
                                              },
                                          }
                                        : {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  attemptLimit: Number(value),
                                              },
                                          }
                                );
                            }}
                        >
                            <SelectTrigger className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {attemptsOptions.map((o) => (
                                    <SelectItem
                                        key={o.value}
                                        value={o.value.toString()}
                                    >
                                        {t(o.keyName, undefined, undefined)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {showAllowSkippingField && (
                <div className="flex items-center gap-x-2 -mb-2">
                    <Checkbox
                        checked={!!selectedInteraction?.content.allowSkipping}
                        onCheckedChange={(checked) =>
                            updateInteraction(
                                selectedInteraction!.id,
                                // Every content shape that lands here has an
                                // optional allowSkipping field; the cast is
                                // needed because TS can't narrow the union via
                                // the runtime showAllowSkippingField check.
                                {
                                    content: {
                                        ...selectedInteraction!.content,
                                        allowSkipping: !!checked,
                                    },
                                } as Partial<typeof selectedInteraction>
                            )
                        }
                    />
                    <Label className="">
                        {t(
                            "interaction-modal.settings.allowSkipping",
                            undefined,
                            undefined
                        )}
                    </Label>
                </div>
            )}

            {showHideCorrectAnswerField && (
                <div className="flex items-center gap-x-2 -mb-2">
                    <Checkbox
                        checked={
                            !!selectedInteraction?.content.hideCorrectAnswer
                        }
                        onCheckedChange={(checked) =>
                            updateInteraction(selectedInteraction!.id, {
                                content: {
                                    ...selectedInteraction?.content,
                                    hideCorrectAnswer: !!checked,
                                },
                            })
                        }
                    />
                    <Label className="">
                        {t(
                            "interaction-modal.settings.hideCorrectAnswer",
                            undefined,
                            undefined
                        )}
                    </Label>
                </div>
            )}

            {showRewindField && isLomda && (
                <LomdaRewindControls
                    scenes={scenes ?? []}
                    currentSceneId={
                        getLomdaRewindSceneId?.(selectedInteraction!.id) ?? null
                    }
                    defaultSceneId={
                        getInteractionGap?.(selectedInteraction!.id) ??
                        scenes?.[0]?.id ??
                        null
                    }
                    onChange={(sceneId) =>
                        setLomdaRewindSceneId?.(
                            selectedInteraction!.id,
                            sceneId
                        )
                    }
                    dir={dir}
                />
            )}

            {showRewindField && !isLomda && (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-x-2">
                        <Checkbox
                            checked={
                                typeof selectedInteraction?.content
                                    .rewindTimestamp === "number"
                            }
                            onCheckedChange={(checked) =>
                                updateInteraction(
                                    selectedInteraction!.id,
                                    // both objects are exactly the same, this is just for ts because audio content is different than open/close question content
                                    selectedInteraction.type === "audio"
                                        ? {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  rewindTimestamp: checked
                                                      ? 0
                                                      : null,
                                              },
                                          }
                                        : {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  rewindTimestamp: checked
                                                      ? 0
                                                      : null,
                                              },
                                          }
                                )
                            }
                        />
                        <Label className="">
                            {t(
                                "interaction-modal.settings.repeatUntilCorrect",
                                undefined,
                                undefined
                            )}
                        </Label>
                    </div>

                    {typeof selectedInteraction?.content.rewindTimestamp ===
                        "number" && (
                        <TimestampInput
                            valueInSeconds={
                                selectedInteraction?.content.rewindTimestamp
                            }
                            onValueChange={(value) =>
                                updateInteraction(
                                    selectedInteraction!.id,
                                    // both objects are exactly the same, this is just for ts because audio content is different than open/close question content
                                    selectedInteraction.type === "audio"
                                        ? {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  rewindTimestamp: value,
                                              },
                                          }
                                        : {
                                              content: {
                                                  ...selectedInteraction?.content,
                                                  rewindTimestamp: value,
                                              },
                                          }
                                )
                            }
                            triggerClassName="w-1/2"
                            showMs
                        />
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Lomda-mode rewind UI: scene picker instead of timestamp picker.
 * Lomda interactions don't know real audio timestamps until render time —
 * we store the target scene id, and the render engine resolves it.
 */
function LomdaRewindControls({
    scenes,
    currentSceneId,
    defaultSceneId,
    onChange,
    dir,
}: {
    scenes: { id: string; title: string }[];
    currentSceneId: string | null;
    defaultSceneId: string | null;
    onChange: (sceneId: string | null) => void;
    dir: "ltr" | "rtl";
}) {
    const enabled = currentSceneId != null;
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-x-2">
                <Checkbox
                    checked={enabled}
                    onCheckedChange={(checked) => {
                        if (checked) {
                            onChange(defaultSceneId);
                        } else {
                            onChange(null);
                        }
                    }}
                />
                <Label className="">
                    {t(
                        "interaction-modal.settings.repeatUntilCorrect",
                        undefined,
                        undefined
                    )}
                </Label>
            </div>

            {enabled && (
                <Select
                    dir={dir}
                    value={currentSceneId ?? undefined}
                    onValueChange={(value) => onChange(value)}
                >
                    <SelectTrigger className="h-9 w-1/2">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {scenes.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                                {s.title}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}
